import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { errMsg } from '../lib/errors';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Percent, Info } from 'lucide-react';
import { toast } from 'sonner';

export default function TaxGroups() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', gst_rate: 18, description: '', active: true });

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/tax-groups'); setItems(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm({ name: '', gst_rate: 18, description: '', active: true }); setDialog(true); };
    const openEdit = (t) => { setEditing(t); setForm({ name: t.name, gst_rate: t.gst_rate, description: t.description || '', active: t.active }); setDialog(true); };

    const save = async () => {
        try {
            if (editing) await api.patch(`/tax-groups/${editing.id}`, form);
            else await api.post('/tax-groups', form);
            toast.success('Saved'); setDialog(false); load();
        } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    };
    const doDelete = async (t) => {
        if (!window.confirm(`Delete tax group "${t.name}"?`)) return;
        try { await api.delete(`/tax-groups/${t.id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    };

    return (
        <AppShell pageTitle="Tax Groups (GST)" pageActions={
            <Button onClick={openNew} data-testid="add-tax-group-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                <Plus className="h-4 w-4 mr-2" />Add Tax Group
            </Button>
        }>
            <div className="rounded-lg border bg-blue-50 border-blue-200 p-3 mb-4 flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 text-blue-700 shrink-0" />
                <div className="text-xs text-blue-900">
                    Tax Groups are pure <strong>GST rate presets</strong>. Assign a group to each product to compute tax at checkout.
                    <br />CGST + SGST = GST (split equally) for <strong>intra-state</strong> orders. IGST = full GST for <strong>inter-state</strong>.
                    <br /><strong>HSN codes</strong> now live on <strong>Categories</strong> (auto-inherited by products) — no longer stored here.
                </div>
            </div>
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={Percent} title="No tax groups" description="Create tax groups (e.g. GST 5%, 12%, 18%) to map to products." />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tax-groups-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Name</th>
                                <th className="text-right font-medium">GST %</th>
                                <th className="text-right font-medium">CGST</th>
                                <th className="text-right font-medium">SGST</th>
                                <th className="text-right font-medium">IGST</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-right p-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(t => (
                                <tr key={t.id} className="border-b hover:bg-neutral-50" data-testid={`tax-row-${t.name}`}>
                                    <td className="p-3"><div className="font-medium">{t.name}</div><div className="text-xs text-neutral-500">{t.description}</div></td>
                                    <td className="text-right tabular-nums font-medium">{t.gst_rate}%</td>
                                    <td className="text-right tabular-nums">{t.cgst_rate}%</td>
                                    <td className="text-right tabular-nums">{t.sgst_rate}%</td>
                                    <td className="text-right tabular-nums">{t.igst_rate}%</td>
                                    <td>{t.active ? <span className="text-green-700 text-xs">Active</span> : <span className="text-neutral-500 text-xs">Inactive</span>}</td>
                                    <td className="p-3 text-right">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => doDelete(t)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? 'Edit Tax Group' : 'New Tax Group'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. GST 18% - Standard" data-testid="tax-name-input" /></div>
                        <div><Label>GST % *</Label><Input type="number" step="0.01" value={form.gst_rate} onChange={e => setForm({ ...form, gst_rate: parseFloat(e.target.value) || 0 })} data-testid="tax-rate-input" /></div>
                        <div className="text-xs text-neutral-500 rounded bg-neutral-50 border p-2">
                            CGST/SGST split = <strong>{(form.gst_rate / 2).toFixed(2)}%</strong> each &middot; IGST = <strong>{Number(form.gst_rate).toFixed(2)}%</strong>
                        </div>
                        <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="E.g. Standard rate for garden tools & pots" /></div>
                        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label>Active</Label></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="save-tax-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
